<script lang="ts">
import { type Instrument, InstrumentState } from '~/models/instruments';

type Props = {
  instrument: Instrument;
};
</script>

<script setup lang="ts">
import { NuxtLink } from '#components';
import Badge from '~/components/common/Badge.vue';

const isNuxtLink = (component: any): component is InstanceType<typeof NuxtLink> =>
  typeof component.$el !== 'undefined';

const props = defineProps<Props>();

const {
  public: { cdnUrl },
} = useRuntimeConfig();
const { d, t } = useI18n();
const localePath = useLocalePath();
const authStore = useAuthStore();
const { isLoggedIn } = storeToRefs(authStore);

const instrumentCardRef = ref<InstanceType<typeof NuxtLink> | HTMLElement | null>(null);
const intersectionObserver = ref<IntersectionObserver | null>(null);
const canShowImage = ref(false);

const { icons, stateToColor, stateToSubscriptionState } = useInstrumentsConstants();
const { stateText, showHiddenResultsUntil } = useInstrumentStateText(toRef(props.instrument));

const isInPreparation = computed(() => props.instrument.State === InstrumentState.InPreparation);
const isHidden = computed(() => props.instrument.State === InstrumentState.Hidden);
const isBeingSubscribed = computed(
  () => props.instrument.State === InstrumentState.BeingSubscribed,
);
const isSold = computed(() => props.instrument.State === InstrumentState.Sold);
const isPartlySold = computed(() => props.instrument.State === InstrumentState.PartlySold);
const isSubscribed = computed(() => props.instrument.State === InstrumentState.Subscribed);
const isUnsuccessful = computed(
  () => props.instrument.State === InstrumentState.SubscriptionUnsuccessful,
);

const previewIcon = computed(() => {
  const tag = (
    props.instrument.Tags?.find((tag) => tag.Name.toLocaleLowerCase().includes('-hidden'))?.Name ||
    'generic-hidden'
  )
    .toLocaleLowerCase()
    .replace('-hidden', '') as keyof typeof icons;

  return icons[tag];
});
const image = `${cdnUrl}/images/gallery/${props.instrument.Slug}/g1.jpg`;
const canShowProgress = computed(
  () => props.instrument.State !== InstrumentState.Hidden && !props.instrument.HideResults,
);

const progress = useInstrumentsProgress(toRef(props.instrument));
const status = computed(() =>
  t(`instrument-card.tags.state.${InstrumentState[props.instrument.State]}`),
);
const statusColor = computed(() => stateToColor[props.instrument.State]);
const instrumentGroupNames = props.instrument.InstrumentGroups.map((group) => group.Name);
const buttonAttributes = computed(() => ({
  disabled: isHidden.value,
  as: isHidden.value ? ('button' as const) : ('NuxtLink' as const),
  ...(!isHidden.value
    ? {
        to: localePath({
          name: isLoggedIn.value
            ? 'dashboard-investments-investments-offer-slug'
            : 'investments-offer-slug',
          params: { slug: props.instrument.Slug },
        }),
      }
    : {}),
}));
const subscriptionState = computed(() => {
  if (
    props.instrument.HideResults &&
    props.instrument.State !== InstrumentState.EvaluatingResults
  ) {
    return t('instrument-card.data.subscription-state.CollectingOrders', {
      time: d(new Date(props.instrument.HiddenResultsUntil ?? new Date()), {
        hour: 'numeric',
        minute: 'numeric',
      }),
    });
  }

  return t(
    `instrument-card.data.subscription-state.${stateToSubscriptionState[props.instrument.State]}`,
  );
});
const component = computed(() => (isHidden.value ? 'div' : resolveComponent('NuxtLink')));
const componentAttrs = computed(() => ({
  ...(isHidden.value
    ? {}
    : {
        to: localePath({
          name: isLoggedIn.value
            ? 'dashboard-investments-investments-offer-slug'
            : 'investments-offer-slug',
          params: { slug: props.instrument.Slug },
        }),
        'aria-label': t('instrument-card.aria.go-to-detail', { name: props.instrument.Name }),
      }),
}));

onMounted(() => {
  if (instrumentCardRef.value) {
    intersectionObserver.value = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!canShowImage.value && entry.intersectionRatio > 0) {
            canShowImage.value = true;

            intersectionObserver.value!.disconnect();
            intersectionObserver.value = null;
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      },
    );

    intersectionObserver.value.observe(
      isNuxtLink(instrumentCardRef.value) ? instrumentCardRef.value.$el : instrumentCardRef.value,
    );
  }
});

onUnmounted(() => {
  intersectionObserver.value?.disconnect();
});
</script>

<template>
  <component
    :is="component"
    v-bind="componentAttrs"
    ref="instrumentCardRef"
    class="instrument"
    :class="{
      'instrument--sold': isSold || isUnsuccessful || isPartlySold,
      'instrument--hidden': isHidden,
    }"
  >
    <h3 class="instrument__name">{{ instrument.Name }}</h3>

    <div class="instrument__tags">
      <Badge :color="statusColor">{{ status }}</Badge>
      <Badge v-for="groupName in instrumentGroupNames" :key="groupName" color="gray">{{
        groupName
      }}</Badge>
    </div>

    <div v-if="isHidden" class="instrument__image" v-html="previewIcon" />
    <div v-else class="instrument__image" :style="canShowImage ? `--image: url('${image}')` : ''">
      <div
        v-if="canShowProgress && progress > 0 && progress < 100"
        class="instrument__progress-wrapper"
      >
        <div class="instrument__progress" :style="`--width: ${progress}%`" />
      </div>

      <div
        v-if="isSold || isPartlySold"
        class="instrument__progress-wrapper instrument__progress-wrapper--sold"
      >
        <span>{{ $t('instrument-card.data.sell-profit') }}</span>
        <span>{{ $n(instrument.SellProfit || 0, 'percentTwoDecimals') }}</span>
      </div>
    </div>

    <div v-if="isHidden || isInPreparation" class="instrument__data instrument__data--preparing">
      <div class="state">
        <span class="state__data">&nbsp;</span>
      </div>

      <div class="state">
        <span class="state__data state__data--white state__data--bold">
          {{ stateText }}
        </span>
      </div>

      <div class="state">
        <span class="state__data">&nbsp;</span>
      </div>
    </div>

    <div
      v-if="isBeingSubscribed && showHiddenResultsUntil"
      class="instrument__data instrument__data--preparing"
      :class="{
        'instrument__data--show-hidden-results-until': showHiddenResultsUntil,
      }"
    >
      <div class="state">
        <span class="state__data">
          {{ stateText }}
        </span>
      </div>

      <div class="state">
        <span class="state__data">&nbsp;</span>
      </div>
    </div>

    <div
      v-if="isBeingSubscribed && !showHiddenResultsUntil"
      class="instrument__data"
      :class="{
        'instrument__data--exceeded-progress': progress >= 100,
      }"
    >
      <div class="state">
        <span class="state__data">{{ subscriptionState }}</span>
        <span v-if="canShowProgress" class="state__data">{{ $n(progress / 100, 'percent') }}</span>
      </div>

      <div class="state">
        <span class="state__data">{{ $t('instrument-card.data.amount') }}</span>
        <span class="state__data">{{
          $n(instrument.Amount || 0, `currencyNoDecimals-${instrument.Currency}`)
        }}</span>
      </div>

      <div class="state">
        <span class="state__data">
          {{ $t('instrument-card.data.remaining') }}
        </span>
        <span class="state__data">{{ stateText }}</span>
      </div>
    </div>

    <div
      v-if="isSubscribed || isUnsuccessful"
      class="instrument__data"
      :class="{ 'instrument__data--sold': isUnsuccessful }"
    >
      <div v-if="isUnsuccessful" class="state">
        <span class="state__data">{{ subscriptionState }}</span>
        <span v-if="canShowProgress" class="state__data">{{ $n(progress / 100, 'percent') }}</span>
        <span v-else class="state__data" />
      </div>

      <div class="state">
        <span class="state__data">{{ $t('instrument-card.data.amount') }}</span>
        <span class="state__data">{{
          $n(instrument.Amount || 0, `currencyNoDecimals-${instrument.Currency}`)
        }}</span>
      </div>

      <template v-if="isSubscribed">
        <div class="state">
          <span class="state__data">{{
            $t('investment-offer-detail.instrument-card.investors-count')
          }}</span>

          <span v-if="instrument.FEInvestorsCount" class="state__data">{{
            $n(instrument.FEInvestorsCount, 'noDecimals')
          }}</span>
        </div>

        <div class="state">
          <span class="state__data">Upsáno dne</span>
          <span class="state__data">{{
            $d(new Date(instrument.EndOfSubscription ?? Date.now()), {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
            })
          }}</span>
        </div>
      </template>

      <div v-if="isUnsuccessful" class="state">
        <span class="state__data">
          {{ $t('instrument-card.tags.state.SubscriptionUnsuccessful') }}
        </span>
        <span class="state__data" />
      </div>
    </div>

    <div v-if="isSold" class="instrument__data instrument__data--sold">
      <div class="state">
        <span class="state__data">{{ $t('instrument-card.data.sell-profit') }}</span>
        <span class="state__data">{{ $n(instrument.SellProfit || 0, 'percentTwoDecimals') }}</span>
      </div>

      <div class="state">
        <span class="state__data">{{ $t('instrument-card.data.sell-price') }}</span>
        <span class="state__data">{{
          $n(instrument.SellPrice || 0, `currencyNoDecimals-${instrument.Currency}`)
        }}</span>
      </div>

      <div class="state">
        <span class="state__data">{{ $t('instrument-card.data.sell-date') }}</span>
        <span class="state__data">{{
          instrument.SellDate ? $d(new Date(instrument.SellDate)) : ''
        }}</span>
      </div>
    </div>

    <div v-if="isPartlySold" class="instrument__data instrument__data--sold">
      <div class="state">
        <span class="state__data">Zhodnocení</span>
        <span class="state__data">{{
          $n(instrument.PartlySellProfit || 0, 'percentTwoDecimals')
        }}</span>
      </div>

      <div class="state">
        <span class="state__data">Počet prodaných kusů</span>
        <span class="state__data">{{
          $t('global.pieces.abbreviation', {
            count: instrument.PartlySellPieces || 0,
            n: $n(instrument.PartlySellPieces || 0, 'noDecimals'),
          })
        }}</span>
      </div>

      <div class="state">
        <span class="state__data">Zbývající počet kusů</span>
        <span class="state__data">{{
          $t('global.pieces.abbreviation', {
            count: instrument.RestOfPieces || 0,
            n: $n(instrument.RestOfPieces || 0, 'noDecimals'),
          })
        }}</span>
      </div>
    </div>

    <Button v-bind="buttonAttributes">{{ $t('instrument-card.go-to-detail') }}</Button>
  </component>
</template>

<style lang="scss" scoped>
.instrument {
  --color: var(--white);

  display: block;

  width: 100%;

  padding: 1.875rem;

  border: 0.0625rem solid transparent;
  border-radius: 0.75rem;

  text-decoration: none;

  &:hover {
    border-color: var(--brown-lighter);

    &:not(.instrument--hidden).instrument__name {
      color: var(--brown-lightest);
    }
  }

  .instrument__name {
    display: -webkit-box;

    margin-bottom: 0.625rem;

    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;

    width: 100%;
    // h3's font size
    min-height: calc(var(--font-size-xl) * 2.4);

    overflow: hidden;
  }

  .instrument__tags {
    display: flex;
    align-items: flex-start;

    margin-bottom: 1.25rem;

    // font-size * 2 lines * line-height + (2 * border) + (2 * padding)
    min-height: calc(var(--font-size-xs) * 2 * 1.5 + (0.0625rem * 2) + (0.0625rem * 2));

    .badge:not(:last-of-type) {
      margin-right: 0.625rem;
    }
  }

  .instrument__image {
    position: relative;

    height: 15rem;

    border-radius: 0.375rem;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center center;
    background-image: var(--image);

    text-align: center;

    :deep(svg) {
      width: inherit;
      height: inherit;
    }
  }

  .instrument__progress-wrapper {
    position: absolute;
    right: 0.125rem;
    bottom: 0.125rem;
    left: 0.125rem;

    height: 0.5rem;

    border-radius: 0.375rem;
    overflow: hidden;

    .instrument__progress {
      width: inherit;
      height: inherit;

      background-color: rgba(0, 0, 0, 0.4);

      &::after {
        content: ' ';

        display: block;

        width: var(--width);
        height: inherit;

        background-color: var(--red-basic);
      }
    }
  }

  .instrument__progress-wrapper--sold {
    inset: 0.125rem;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    height: unset;

    text-align: center;
    color: var(--white);
    font-weight: bold;
    font-size: var(--font-size-lg);
    text-shadow: 1px 1px 2px var(--black);
  }

  .instrument__data {
    margin-top: 1.25rem;
    margin-bottom: 1.25rem;

    .state {
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      @media (min-width: 768px) {
        flex-direction: row;
      }

      .state__data {
        &--white {
          color: var(--white);
        }

        &--bold {
          font-weight: bold;
        }

        &:nth-of-type(2n-1):not(.state__data--white),
        &:nth-of-type(2n-1):not(.state__data--bold) {
          color: var(--brown-lighter);
          font-weight: 500;
        }

        &:nth-child(2n) {
          color: var(--white);
          font-weight: 700;
        }
      }
    }

    &--preparing {
      .state {
        justify-content: center;
        text-align: center;
      }
    }

    &--exceeded-progress {
      .state:nth-child(1) {
        .state__data:nth-child(2) {
          color: var(--red-bright);
        }
      }
    }

    &--sold {
      .state {
        .state__data:nth-child(2n) {
          color: var(--brown-lighter);
        }
      }
    }

    &--show-hidden-results-until {
      .state:nth-child(1) {
        flex-direction: column;

        .state__date:nth-child(2) {
          text-align: left;
        }
      }
    }
  }

  .button {
    width: 100%;

    justify-content: center;
  }

  &--hidden {
    .instrument__name {
      color: var(--brown-light);
    }
  }

  &--sold {
    position: relative;

    .instrument__image::before {
      content: '';

      position: absolute;
      inset: 0;

      background-color: rgb(0 0 0 / 0.6);
    }

    .instrument__data {
      span:nth-of-type(2n-1),
      span:nth-of-type(2n) {
        color: var(--brown-light);
      }
    }
  }
}
</style>
